import { useState, useRef } from "react";
import { ArrowRight, ArrowLeft, Calendar, Download, ExternalLink, Quote, Ruler, Smartphone, Tablet } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { PageFooter } from "@/components/PageFooter";
import { getProduct } from "@/data/products";
import { useLang } from "@/i18n";

const SCHEDULE_MAILTO = "martina.lovetinska@cchellenic.com,pero.pehar@master-fb.com,igor.guberovic@cchellenic.com,katarzyna.stanczak@cchellenic.com";

export const ProductPage = ({ productId, onNavigate }) => {
  const { t } = useLang();
  const product = getProduct(productId);
  const [activeDep, setActiveDep] = useState(null);
  const closeTimerRef = useRef(null);

  const openDep = (idx) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setActiveDep(idx);
  };
  const scheduleCloseDep = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setActiveDep(null), 120);
  };

  if (!product) {
    return (
      <div className="text-center py-20">
        <p className="text-sm text-[#555555]">{t("Product not found.")}</p>
      </div>
    );
  }

  const mailto = `mailto:${SCHEDULE_MAILTO}?subject=${encodeURIComponent(product.schedule_subject)}&body=${encodeURIComponent(product.schedule_body)}`;

  return (
    <div data-testid={`product-page-${product.id}`}>
      <button
        onClick={() => onNavigate("home")}
        className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#555555] hover:text-[#dd260e] mb-10 transition-colors"
        data-testid="back-to-category-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("All products")}
      </button>

      {/* Title */}
      <div className="mb-10">
        <span className="cchbc-eyebrow mb-6">{t(product.eyebrow)}</span>
        <h1
          className="font-condensed text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight text-[#000000] mb-4 mt-5 leading-[0.9]"
          data-testid="product-title"
        >
          {product.headline}
        </h1>
        {product.hero_title && (
          <p className="font-condensed text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#dd260e] mb-5">
            {t(product.hero_title)}
          </p>
        )}
        <div className="flex items-center gap-2 mb-5">
          {product.tags.map((t2) => (
            <span
              key={t2}
              className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#555555] border border-[#e8e8e8] px-2 py-1 rounded-sm"
              data-testid={`tag-${t2.toLowerCase()}`}
            >
              {t2}
            </span>
          ))}
        </div>
        <p className="text-[#555555] text-base sm:text-lg leading-relaxed max-w-2xl">
          {t(product.description)}
        </p>
      </div>

      {/* Benefit numbers */}
      <div
        className="grid grid-cols-2 lg:grid-cols-4 gap-0 mb-14 border-t border-b border-[#e8e8e8]"
        data-testid="benefit-numbers"
      >
        {product.benefits.map((b, i) => (
          <div
            key={b.label}
            className={`bg-white p-6 sm:p-7 ${
              i < product.benefits.length - 1 ? "border-b lg:border-b-0 lg:border-r border-[#e8e8e8]" : ""
            } ${i === 1 ? "border-b lg:border-b-0" : ""}`}
          >
            <b.icon className="w-5 h-5 text-[#dd260e] mb-4" strokeWidth={2} />
            <p className="font-condensed text-4xl font-black uppercase tracking-tight text-[#000000]">
              {b.value}
            </p>
            <p className="text-[10px] text-[#555555] mt-2 leading-snug uppercase tracking-[0.1em] font-semibold">
              {t(b.label)}
            </p>
          </div>
        ))}
      </div>

      {/* CTA banner */}
      <div
        className="bg-[#000000] rounded-sm p-8 sm:p-10 mb-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
        data-testid="get-it-section"
      >
        <div>
          <span className="cchbc-eyebrow light mb-3">{t("See it live")}</span>
          <p className="font-condensed text-white font-black text-2xl uppercase tracking-tight mt-3 mb-2">
            {t("Bring")} {product.name} {t("to your market")}
          </p>
          <p className="text-white/60 text-sm max-w-md">
            {t("Talk to the FORA team to scope a pilot — hardware, installation and content included.")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4 shrink-0">
          {product.demo_url && (
            <a
              href={product.demo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="cchbc-link light"
              data-testid="live-demo-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" /> {product.demo_label ? t(product.demo_label) : t("Live demo")}
            </a>
          )}
          <a href={mailto} className="cchbc-btn cchbc-btn-outline-light" data-testid="schedule-meeting-btn">
            <Calendar className="w-3.5 h-3.5" /> {t("Schedule call")}
          </a>
        </div>
      </div>

      {/* Spec Sheets / Catalogues */}
      {product.spec_sheets && product.spec_sheets.length > 0 && (
        <div className="mb-14" data-testid="spec-sheets-section">
          <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
            <span className="cchbc-eyebrow mb-3">{t("Spec Sheets & Catalogues")}</span>
            <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
              {t("Download technical resources")}
            </h2>
            <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
              {t("Full technical documentation for procurement, IT and installation teams.")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {product.spec_sheets.map((doc) => (
              <a
                key={doc.href}
                href={doc.href}
                download={doc.filename}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-[#e8e8e8] rounded-sm p-5 hover:border-[#000000] transition-colors group flex items-start gap-4"
                data-testid={`spec-sheet-${doc.filename.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
              >
                <div className="w-12 h-12 bg-[#dd260e] flex items-center justify-center rounded-sm shrink-0">
                  <Download className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-condensed text-lg font-black uppercase tracking-tight text-[#000000] mb-1 group-hover:text-[#dd260e] transition-colors">
                    {t(doc.title)}
                  </p>
                  <p className="text-xs text-[#555555] leading-relaxed mb-3">{t(doc.description)}</p>
                  <div className="flex items-center gap-3 text-[10px] text-[#555555] font-bold uppercase tracking-[0.15em]">
                    <span>PDF</span>
                    {doc.size && (
                      <>
                        <span className="text-[#e8e8e8]">·</span>
                        <span>{doc.size}</span>
                      </>
                    )}
                    {doc.pages && (
                      <>
                        <span className="text-[#e8e8e8]">·</span>
                        <span>{doc.pages}</span>
                      </>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Gallery */}
      {product.gallery && product.gallery.length > 0 && (
        <div className="mb-14" data-testid="product-gallery">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-2 auto-rows-[100px] sm:auto-rows-[140px]">
            {product.gallery.map((img, i) => (
              <div key={i} className={`${img.span} rounded-sm overflow-hidden ${img.bg || ""}`}>
                <img
                  src={img.src}
                  alt={img.alt}
                  className={`w-full h-full ${img.fit === "contain" ? "object-contain p-3" : "object-cover"} hover:scale-105 transition-transform duration-500`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features + Dependencies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-14 border-t border-[#e8e8e8]">
        <div
          className="bg-white p-8 lg:border-r border-b lg:border-b-0 border-[#e8e8e8]"
          data-testid="key-features-card"
        >
          <span className="cchbc-eyebrow mb-6">{t("Key Features")}</span>
          <ul className="space-y-4 mt-6">
            {product.features.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-4 pb-4 border-b border-[#e8e8e8] last:border-b-0 last:pb-0"
              >
                <f.icon className="w-5 h-5 text-[#dd260e] shrink-0 mt-0.5" strokeWidth={1.8} />
                <span className="text-sm text-[#000000] font-medium">{t(f.text)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white p-8" data-testid="dependencies-card">
          <span className="cchbc-eyebrow mb-3">{t("Dependencies & Linked Products")}</span>
          <p className="text-sm text-[#555555] leading-relaxed max-w-md mt-4 mb-6">
            {t("FORA ecosystem combines hardware, cloud and content into a single scalable platform.")}
          </p>
          <div className="flex flex-wrap gap-2 mb-8">
            {product.dependencies.map((d, idx) => (
              <Popover
                key={d.name}
                open={activeDep === idx}
                onOpenChange={(o) => setActiveDep(o ? idx : null)}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onMouseEnter={() => openDep(idx)}
                    onMouseLeave={scheduleCloseDep}
                    aria-label={`${d.name} \u2014 details`}
                    className="group inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#e8e8e8] text-[#000000] text-xs font-medium rounded-sm hover:border-[#000000] focus:border-[#dd260e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#dd260e]/30 transition-colors cursor-pointer"
                    data-testid={`dependency-tag-${d.name
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/(^-|-$)/g, "")}`}
                  >
                    {d.name}
                    <span
                      className="w-1 h-1 rounded-full bg-[#dd260e] group-hover:scale-125 transition-transform"
                      aria-hidden="true"
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  sideOffset={8}
                  collisionPadding={16}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                  onMouseEnter={() => openDep(idx)}
                  onMouseLeave={scheduleCloseDep}
                  className="w-[min(320px,calc(100vw-32px))] border-[#000000] bg-[#000000] text-white p-4 rounded-sm shadow-xl"
                  data-testid={`dependency-popover-${d.name
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/(^-|-$)/g, "")}`}
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#dd260e] mb-2">
                    {d.name}
                  </p>
                  <p className="text-xs text-white/80 leading-relaxed">{t(d.desc)}</p>
                </PopoverContent>
              </Popover>
            ))}
          </div>
          <span className="cchbc-eyebrow mb-4">{t("Active in Markets")}</span>
          <div className="flex flex-wrap gap-2 mb-6 mt-4">
            {product.markets.map((m) => (
              <span
                key={m}
                className="px-2.5 py-1 bg-[#000000] text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-sm"
              >
                {m}
              </span>
            ))}
          </div>
          {product.screen_sizes && product.screen_sizes.length > 0 && (
            <>
              <span className="cchbc-eyebrow mb-4">{t("Available Sizes")}</span>
              <div className="flex flex-wrap gap-2 mt-4">
                {product.screen_sizes.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#e8e8e8] text-[#000000] text-[11px] font-bold rounded-sm"
                    data-testid={`size-${s.replace(/[^a-z0-9]+/gi, "")}`}
                  >
                    <Ruler className="w-3 h-3 text-[#dd260e]" /> {s}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Quote */}
      {product.quote && (
        <div className="bg-white border border-[#e8e8e8] rounded-sm p-8 sm:p-10 mb-14" data-testid="customer-quote">
          <Quote className="w-6 h-6 text-[#dd260e] mb-4" strokeWidth={1.5} />
          <p className="text-lg sm:text-xl leading-relaxed text-[#000000] font-medium max-w-3xl mb-6">
            {t(product.quote.text)}
          </p>
          <div className="flex items-center gap-3 pt-5 border-t border-[#e8e8e8]">
            <span className="w-8 h-[2px] bg-[#dd260e]" />
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.12em] text-[#000000]">
                {product.quote.author}
              </p>
              <p className="text-xs text-[#555555]">{t(product.quote.role)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Guest Intelligence — 8 numbered cards */}
      {product.guest_intelligence && (
        <div className="mb-14" data-testid="guest-intelligence-section">
          <div className="mb-8 pb-4 border-b border-[#e8e8e8]">
            <span className="cchbc-eyebrow mb-3">{product.guest_intelligence.eyebrow}</span>
            <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
              {product.guest_intelligence.headline}
            </h2>
            <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
              {product.guest_intelligence.description}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {product.guest_intelligence.items.map((item) => (
              <div
                key={item.num}
                className="bg-white border border-[#e8e8e8] rounded-sm p-5 hover:border-[#000000] transition-colors group"
                data-testid={`guest-card-${item.num}`}
              >
                <span className="font-condensed text-xs font-black tracking-tight text-white bg-[#dd260e] px-2 py-0.5 rounded-sm inline-block mb-3">
                  {item.num}
                </span>
                <p className="font-condensed text-base font-bold uppercase tracking-tight text-[#000000] mb-2 group-hover:text-[#dd260e] transition-colors">
                  {item.title}
                </p>
                <p className="text-[11px] text-[#555555] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* App Download Links */}
      {product.app_links && (
        <div className="mb-14" data-testid="app-links-section">
          <div className="mb-8 pb-4 border-b border-[#e8e8e8]">
            <span className="cchbc-eyebrow mb-3">{product.app_links.eyebrow}</span>
            <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
              {product.app_links.headline}
            </h2>
            <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
              {product.app_links.description}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {product.app_links.links.map((link) => {
              const Icon = link.label.toLowerCase().includes("tablet") ? Tablet : Smartphone;
              return (
                <a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-[#e8e8e8] rounded-sm p-5 hover:border-[#000000] transition-colors group flex items-start gap-4"
                  data-testid={`app-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                >
                  <Icon className="w-5 h-5 text-[#dd260e] shrink-0 mt-0.5" strokeWidth={1.8} />
                  <div className="flex-1 min-w-0">
                    <p className="font-condensed text-base font-bold uppercase tracking-tight text-[#000000] group-hover:text-[#dd260e] transition-colors">
                      {link.label}
                    </p>
                    <p className="text-[11px] text-[#555555] mt-1 truncate">{link.platform}</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-[#e8e8e8] group-hover:text-[#dd260e] transition-colors shrink-0 mt-1" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA Footer */}
      <div className="pt-10 border-t border-[#e8e8e8]" data-testid="cta-footer">
        <span className="cchbc-eyebrow mb-6">{t("Next Steps")}</span>
        <div className="flex flex-wrap items-center gap-6 mt-6">
          <a href={mailto} className="cchbc-btn cchbc-btn-primary" data-testid="schedule-meeting-btn-bottom">
            <Calendar className="w-3.5 h-3.5" /> {t("Schedule a meeting")}
          </a>
          <button
            type="button"
            onClick={() => {
              const original = document.title;
              document.title = `FORA-${product.name.replace(/\s+/g, "-")}-Product-Brief`;
              window.print();
              setTimeout(() => { document.title = original; }, 200);
            }}
            className="cchbc-link"
            data-testid="download-pdf-btn"
          >
            <Download className="w-3.5 h-3.5" /> {t("Download product brief (PDF)")}
          </button>
        </div>
      </div>

      <PageFooter />
    </div>
  );
};
